import { useState } from 'react'
import { ClockRewindHistory, NewChat } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { TruncatedText } from '@/components/truncated-text/TruncatedText'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import AgentV2ConversationMenu from '@/agent-v2/conversations/components/AgentV2ConversationMenu'
import AgentV2ConversationDialog from '@/agent-v2/conversations/components/AgentV2ConversationDialog'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectAgentV2ConversationFromId } from '@/agent-v2/conversations/store/selectors'
import { postCreateTableChat } from '../fetch/post-create-table-chat'
import TableChatPicker from './TableChatPicker'

/** Actions for the assistant beside a table, independently of the selected source tab. */
export default function TableChatHeader({
  tableId,
  matterId,
  conversationId,
  onOpenChat,
  onDeleted,
  disabled = false,
}: {
  tableId: string
  matterId: string
  conversationId: string | null
  onOpenChat: (id: string) => void
  onDeleted: () => void
  disabled?: boolean
}) {
  const dispatch = useAppDispatch()
  const conversation = useAppSelector((state) => selectAgentV2ConversationFromId(state, { conversationId: conversationId ?? undefined }))
  const [historyOpen, setHistoryOpen] = useState(false)
  const [dialog, setDialog] = useState<'rename' | 'delete' | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const createChat = async () => {
    if (creating) return
    setCreating(true)
    setError(null)
    try {
      const chat = await postCreateTableChat(tableId)
      dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: true }))
      onOpenChat(chat.id)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to create chat.')
    } finally {
      setCreating(false)
    }
  }
  return (
    <>
      <div role="toolbar" aria-label="Table chat actions" className="flex h-[43px] min-w-0 shrink-0 items-center gap-2 border-b border-brand-neutral-200 px-3">
        <div className="min-w-0 flex-1 text-sm font-semibold text-brand-neutral-950">
          {conversation && <TruncatedText placement="bottom-start">{conversation.title}</TruncatedText>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
            <PopoverTrigger asChild>
              <IconButton
                disabled={disabled}
                icon={<ClockRewindHistory />}
                variant="plain"
                color="neutral"
                size="sm"
                aria-label="Chat history"
                tooltipText="Chat history"
              />
            </PopoverTrigger>
            <PopoverContent
              aria-label="Chat history"
              align="end"
              width="auto"
              showArrow={false}
              className="w-60 max-w-[calc(100vw-2rem)] border border-brand-neutral-200 p-2 shadow-md"
            >
              <h2 className="px-2 py-1 text-xs uppercase text-brand-neutral-600">Chat history</h2>
              <TableChatPicker
                tableId={tableId}
                matterId={matterId}
                currentConversationId={conversationId}
                historyOnly
                onOpenChat={(id) => {
                  setHistoryOpen(false)
                  onOpenChat(id)
                }}
              />
            </PopoverContent>
          </Popover>
          <IconButton
            icon={<NewChat />}
            variant="plain"
            color="neutral"
            size="sm"
            aria-label="Create new chat"
            tooltipText="Create new chat"
            loading={creating}
            disabled={disabled}
            onClick={() => void createChat()}
          />
          {conversationId && (
            <AgentV2ConversationMenu
              conversationId={conversationId}
              iconSize="sm"
              tooltipText="Chat actions"
              onSelectRename={() => setDialog('rename')}
              onSelectDelete={() => setDialog('delete')}
            />
          )}
        </div>
      </div>
      {error && (
        <p role="alert" className="px-4 py-2 text-sm text-red-800">
          {error}
        </p>
      )}
      {dialog && (
        <AgentV2ConversationDialog
          conversationId={conversationId}
          kind={dialog}
          open
          onClose={() => setDialog(null)}
          onDeleted={() => {
            setDialog(null)
            onDeleted()
          }}
        />
      )}
    </>
  )
}
